import { Typography, Grid, TableRow, TableCell, useTheme, TableBody, Divider } from "@mui/material";
import React from "react";
import { useState, useEffect, useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import AddEditAddressMutation from "./AddEditAddressMutation/AddEditAddressMutation";
import { State } from "./interfaces";
import { Container, StyledTable, StyledTableCell, Label } from "./styles";
import LoadingOverlay from "../../../containers/LoadingOverlay/LoadingOverlay";
import { EmployeeAddressSTO } from "../../../models/employee-addresses/employee-address-sto";
import { EmployeeAddressMutationSTO } from "../../../models/employee-addresses/mutations/employee-address-mutation-sto";
import { MutationStatusRef } from "../../../models/mutation-statuses/mutation-status-ref";
import AddEditItem from "../../../reusable-components/Table/AddEditItem/AddEditItem";
import { FormField } from "../../../reusable-components/Table/AddEditItem/interfaces";

import { EmployeesAddressesApiService } from "../../../services/employees/employees-addresses-api.service";
import { loadEmployeesAddresses } from "../../../services/employees/employees-addresses-api.service";
import { loadEmployeeCurrentAddressMutations } from "../../../services/employees/employees-current-address-mutations-api.service";

import { MUTATIONS_STATUSES } from "../../../utils/constants/constants";
import { EmployeeDataContext } from "../EmployeePersonalData";

const EmployeeAddress = () => {
  const { t } = useTranslation();
  const tRP = "components.data_and_documents"; // tRP - translation root path
  const theme = useTheme();
  const { employee, forManager } = useContext(EmployeeDataContext);

  const [state, setState] = useState<State>({
    loading: false,
    error: "",
    address: null,
    mutation: null,
    hasMutation: false,
  });

  const getEmployeeAddress = useCallback(
    async (employeeId: string, signal?: AbortSignal) => {
      setState((state) => ({ ...state, loading: true }));

      try {
        const employeesAddressesResponse = await loadEmployeesAddresses(employeeId, forManager, signal);

        const employeeAddresMutations = await loadEmployeeCurrentAddressMutations(employeeId, forManager, signal);

        const address = employeesAddressesResponse.items[0] ?? null;

        const newEmployeeAddressMutation: EmployeeAddressMutationSTO = {
          isDeleted: false,
          id: uuidv4(),
          companyAccount: null,
          legalEntity: employee?.legalEntity ?? null,
          employee,
          original: address,
          addressLine1: address?.addressLine1,
          addressLine2: address?.addressLine2,
          locality: address?.locality,
          region: address?.region,
          zipCode: address?.zipCode,
          status: {
            id: MUTATIONS_STATUSES.PENDING.id,
          } as MutationStatusRef,
          toDate: address?.toDate,
        };

        if (!signal || !signal.aborted) {
          setState((state) => ({
            ...state,
            address,
            hasMutation: !!employeeAddresMutations.items.length,
            mutation: employeeAddresMutations.items[0] ?? newEmployeeAddressMutation,
          }));
        }
      } catch (error) {
        if (!signal || !signal.aborted) setState((state) => ({ ...state, error }));
      } finally {
        if (!signal || !signal.aborted) setState((state) => ({ ...state, loading: false }));
      }
    },
    [employee]
  );

  useEffect(() => {
    const abortController = new AbortController();

    if (employee?.id) getEmployeeAddress(employee.id, abortController.signal);

    return () => abortController.abort();
  }, [employee?.id, getEmployeeAddress]);

  const newAddress: EmployeeAddressSTO = {
    id: uuidv4(),
    isDeleted: false,
    employee,
    companyAccount: employee?.companyAccount ?? null,
    addressLine1: "",
    addressLine2: "",
    locality: "",
    region: "",
    zipCode: "",
    toDate: new Date(9999, 11, 31),
  };

  const formFields: FormField[] = [
    {
      field: "addressLine1",
      labelKey: t(`${tRP}.employee_data.employee_address.fields.addressLine1`),
    },
    {
      field: "addressLine2",
      labelKey: t(`${tRP}.employee_data.employee_address.fields.addressLine2`),
      required: false,
    },
    {
      field: "locality",
      labelKey: t(`${tRP}.employee_data.employee_address.fields.locality`),
    },
    {
      field: "region",
      labelKey: t(`${tRP}.employee_data.employee_address.fields.region`),
    },
    {
      field: "zipCode",
      labelKey: t(`${tRP}.employee_data.employee_address.fields.zipCode`),
    },
    {
      field: "toDate",
      fieldType: "datePicker",
      labelKey: t(`${tRP}.employee_data.employee_address.fields.toDate`),
    },
  ];

  const { address, mutation, hasMutation } = state;

  return (
    <Container>
      <LoadingOverlay loading={state.loading}>
        <StyledTable>
          <TableBody>
            <TableRow>
              <TableCell
                rowSpan={6}
                style={{ verticalAlign: "top", padding: 0, width: "160px" }}
              >
                <Grid
                  style={{
                    borderRadius: "5px",
                    backgroundColor: theme.palette.background.paper,
                    border: "none",
                    padding: "0px 5px",
                    height: "45px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    justifyContent: "center",
                  }}
                >
                  <Typography variant="h6">{t(`${tRP}.employee_data.employee_address.title`)}</Typography>
                  {address && (
                    <AddEditAddressMutation
                      original={address}
                      mutation={mutation}
                      hasMutation={hasMutation}
                      handleReload={() => getEmployeeAddress(employee?.id ?? "")}
                    />
                  )}
                </Grid>
              </TableCell>
              <TableCell
                rowSpan={6}
                style={{ verticalAlign: "top", padding: 0 }}
              >
                <Divider
                  flexItem
                  orientation="horizontal"
                  style={{
                    width: "100%",
                    minWidth: "10px",
                    marginTop: "20px",
                    border: `3px solid ${theme.palette.background.paper}`,
                  }}
                />
              </TableCell>

              <StyledTableCell>
                {address ? (
                  <>
                    <Label variant="body2">Address line 1</Label>
                    <>{`${address?.addressLine1}`}</>
                  </>
                ) : (
                  <AddEditItem
                    titleForEditComponent={`${t(`${tRP}.employee_data.employee_address.add_title`)}`}
                    item={newAddress}
                    formFields={formFields}
                    serviceApiInstance={new EmployeesAddressesApiService()}
                    handleReload={() => getEmployeeAddress(employee?.id ?? "")}
                    customAddEditButtonStyle={{
                      width: "unset",
                      height: "unset",
                      padding: "8px",
                      color: theme.palette.primary.main,
                      backgroundColor: "transparent",
                      borderRadius: theme.shape.borderRadius,
                    }}
                  >
                    <Typography variant={"body1"}>{t(`${tRP}.employee_data.employee_address.add_title`)}</Typography>
                  </AddEditItem>
                )}
              </StyledTableCell>
            </TableRow>
            {address && (
              <>
                <TableRow>
                  <StyledTableCell>
                    <Label variant="body2">Address Line 2</Label>
                    {address?.addressLine2}
                  </StyledTableCell>
                </TableRow>

                <TableRow>
                  <StyledTableCell>
                    <Label variant="body2">Locality</Label>
                    {address?.locality}
                  </StyledTableCell>
                </TableRow>
                <TableRow>
                  <StyledTableCell>
                    <Label variant="body2">Region</Label>
                    {address?.region}
                  </StyledTableCell>
                </TableRow>
                <TableRow>
                  <StyledTableCell>
                    <Label variant="body2">Zip code</Label>
                    {address?.zipCode}
                  </StyledTableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </StyledTable>
      </LoadingOverlay>
    </Container>
  );
};

export default EmployeeAddress;
